import DataModel from "../../model-factory/basic/data/DataModel";
import {SearchBasicDataModel, SearchParams} from "./SearchBasicDataFactory";
import DataChoice from "../../model-factory/basic/data/DataChoice";
import ApiRequestVO from "../../manager/api/ApiRequestVO";
import {getManager} from "../../manager/Manager";
import DataFactory from "../../model-factory/basic/data/DataFactory";
import {LocateReturnType} from "../locate/LocateDataFactory";

class SearchRelatedDataVO
{
    entryIndex: number = -1;
    numberOfEntries: number = 0;
    pageNumber: number = 0;
    previousEntryPageNumber: number = 0;
    nextEntryPageNumber: number = 0;
    parentPage:DataChoice|null = null;
    previousEntry:DataChoice|null = null;
    nextEntry:DataChoice|null = null;
}

export class SearchPageBasicDataModel extends DataModel
{
    searchParams:SearchParams = new SearchParams();

    entryLimit:number = 3;
    actualPageCount:number = 0;
    numberOfEntries:number = 0;

    applyModelConditions()
    {
        super.applyModelConditions();

        this.searchParams.applyModelConditions(this.populateIDs)
    }

    getChildModel(pageNumber?:number, returnType?:string): SearchBasicDataModel|null
    {
        return null;
    }

    getChildModels(): SearchBasicDataModel[]
    {
        const models:SearchBasicDataModel[] = [];
        for(let i = 0; i < this.choiceOrder.length; i ++){
            const pageNumber = i + 1;
            models.push(this.getChildModel(pageNumber)!);
        }
        return models;
    }

    getEntryIndexRequestVO(entryID:string): ApiRequestVO|null
    {
        return null;
    }

    replaceSelectedPageChoiceList(choiceList:DataChoice[]):void
    {
        const childModels = this.getChildModels();
        for(let childModel of childModels)
        {
            let selectedPageChoiceList:DataChoice[] = [];
            for(let choice of choiceList){
                if(childModel.choiceOrder.includes(choice.id)){
                    selectedPageChoiceList.push(choice);
                }
            }
            if(selectedPageChoiceList.length > 0){
                childModel.addToChoiceList(selectedPageChoiceList)
            }
        }
    }

    resetDataFromEntryID(entryID:string):void
    {
        const childModels = this.getChildModels();
        let pageNumber = 0;
        for(let childModel of childModels){
            const hasEntry = childModel.choiceOrder.includes(entryID);
            if(hasEntry){
                pageNumber = childModel.pageNumber;
                break;
            }
        }
        if(!pageNumber){
            return;
        }
        this.resetDataFromPage(pageNumber);
    }

    resetDataFromPage(pageNumber:number = 0):void
    {
        let pageIndex = Math.max(pageNumber - 1, 0);
        const childModels = this.getChildModels();
        for(let i = pageIndex; i < childModels.length; i ++){
            const childModel = childModels[i];
            childModel.resetPopulated();
        }
        this.resetPopulated();
    }

    async getRelatedData(entryID:string): Promise<SearchRelatedDataVO>
    {

        return this.requirePopulated().then(() =>{
            const childModels = this.getChildModels();
            let pageNumber = 0;
            let childModel:SearchBasicDataModel|null = null;
            let entryIndex:number = 0;
            for(let checkChildModel of childModels){
                if(checkChildModel.isPopulated()){
                    let childEntryIndex = checkChildModel.choiceOrder.indexOf(entryID);
                    if(childEntryIndex !== -1){
                        pageNumber = checkChildModel.pageNumber;
                        childModel = checkChildModel;
                        entryIndex += childEntryIndex;
                        break;
                    }
                }
                entryIndex += this.entryLimit;
            }
            const relatedDataVO = new SearchRelatedDataVO();
            relatedDataVO.numberOfEntries = this.numberOfEntries;

            const applyPreviousEntry = (childModel:SearchBasicDataModel, choiceIndex:number): Promise<void> => {
                return new Promise( resolve =>
                {
                    if(choiceIndex > 0){
                        relatedDataVO.previousEntry = childModel.choices[childModel.choiceOrder[choiceIndex - 1]] ?? null;
                        relatedDataVO.previousEntryPageNumber = relatedDataVO.previousEntry ? childModel.pageNumber : 0;
                        resolve();
                        return;
                    }
                    const pageIndex = childModel.pageNumber - 1;
                    const previousChildModel = pageIndex > 0 ? (childModels[pageIndex - 1] ?? null) : null
                    if(!previousChildModel){
                        resolve();
                        return;
                    }

                    previousChildModel.requirePopulated().then(() =>{
                        const choiceIndex = previousChildModel.choiceOrder.length - 1;
                        const choiceID = choiceIndex >= 0 ? previousChildModel.choiceOrder[choiceIndex] : null
                        relatedDataVO.previousEntry = choiceID ? (previousChildModel.choices[choiceID] ?? null) : null;
                        relatedDataVO.previousEntryPageNumber = relatedDataVO.previousEntry ? childModel.pageNumber - 1 : 0;
                        resolve();
                    });
                } );
            }

            const applyNextEntry = (childModel:SearchBasicDataModel, choiceIndex:number): Promise<void> => {
                return new Promise( resolve =>
                {
                    if(choiceIndex < childModel.choiceOrder.length - 1){
                        relatedDataVO.nextEntry = childModel.choices[childModel.choiceOrder[choiceIndex + 1]] ?? null;
                        relatedDataVO.nextEntryPageNumber = relatedDataVO.nextEntry ? childModel.pageNumber : 0;
                        resolve();
                        return;
                    }
                    const pageIndex = childModel.pageNumber - 1;
                    const nextChildModel = pageIndex < childModels.length - 1 ? (childModels[pageIndex  + 1] ?? null) : null
                    if(!nextChildModel){
                        resolve();
                        return;
                    }
                    nextChildModel.requirePopulated().then(() =>{
                        const choiceID = nextChildModel.choiceOrder.length ? nextChildModel.choiceOrder[0] : null
                        relatedDataVO.nextEntry = choiceID ? (nextChildModel.choices[choiceID] ?? null) : null;
                        relatedDataVO.nextEntryPageNumber = relatedDataVO.nextEntry ? childModel.pageNumber + 1 : 0;
                        resolve();
                    });
                } );
            }

            const handlePageDetermined = (childModel:SearchBasicDataModel): Promise<SearchRelatedDataVO> => {
                return new Promise( resolve =>
                {
                    const choiceIndex = childModel?.choiceOrder.indexOf(entryID) ?? -1;
                    if(choiceIndex === -1){
                        resolve(relatedDataVO);
                        return;
                    }
                    relatedDataVO.pageNumber = pageNumber;
                    relatedDataVO.parentPage = this.choices[pageNumber] ?? null;

                    applyPreviousEntry(childModel, choiceIndex).then((previousEntry) =>{
                        applyNextEntry(childModel, choiceIndex).then((nextEntry) =>{
                            resolve(relatedDataVO);
                        });
                    });
                } );
            }

            if(childModel){
                relatedDataVO.entryIndex = entryIndex;
                return handlePageDetermined(childModel);
            }
            entryIndex = -1;
            const requestVO: ApiRequestVO|null = this.getEntryIndexRequestVO(entryID);
            if(!requestVO){
                return relatedDataVO;
            }
            return getManager().api.requestData(requestVO).then((responseVO) => {
                if(responseVO.apiError){
                    return relatedDataVO;
                }
                entryIndex = parseInt(responseVO.response.index ?? -1);
                if(entryIndex < 0){
                    return relatedDataVO;
                }
                relatedDataVO.entryIndex = entryIndex;
                pageNumber = Math.floor(entryIndex / this.entryLimit) + 1;
                childModel = childModels[pageNumber - 1] ?? null;
                if(!childModel){
                    return relatedDataVO;
                }
                return childModel.requirePopulated().then(() =>{
                    return handlePageDetermined(childModel!);
                });
            });
        })
    }
}

export default class SearchPageListDataFactory extends DataFactory
{

    birth(id: string): SearchPageBasicDataModel
    {
        return new SearchPageBasicDataModel(id);
    }

    replaceSelectedPageChoiceList(choiceList:DataChoice[]):void
    {
        for(let modelID in this.conditionModels)
        {
            const model = this.conditionModels[modelID] as SearchPageBasicDataModel;
            model.replaceSelectedPageChoiceList(choiceList);
        }
    }

    resetDataFromEntryID(entryID:string):void
    {
        for(let modelID in this.conditionModels)
        {
            const model = this.conditionModels[modelID] as SearchPageBasicDataModel;
            model.resetDataFromEntryID(entryID);
        }
    }
}